import React from "react";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import DeleteIcon from "@mui/icons-material/Delete";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { DragDropContext, Draggable, Droppable } from "@hello-pangea/dnd";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { getDateRangeDisplayLabel } from "./dateRangePresets";

// Fallback labels for employee metrics (used when availableMetrics doesn't include them)
const EMPLOYEE_METRICS_LABELS = {
  total_shifts_scheduled: { label: "Shifts Scheduled", format: "number" },
  total_scheduled_hours: { label: "Scheduled Hours", format: "hours" },
  actual_hours_clocked: { label: "Actual Hours", format: "hours" },
  total_shifts_worked: { label: "Shifts Worked", format: "number" },
  shifts_traded: { label: "Shifts Traded", format: "number" },
  time_off_taken: { label: "Time Off Days Taken", format: "number" },
  time_off_unpaid_taken: { label: "Unpaid Time Off Days Taken", format: "number" },
  time_off_pto_taken: { label: "PTO Days Taken", format: "number" },
  time_off_sick_taken: { label: "Sick Days Taken", format: "number" },
  missed_shifts: { label: "Shifts Missed (Legacy Combined)", format: "number" },
  unclassified_missed_shifts: { label: "Unclassified Missed Shifts (Needs Review)", format: "number" },
  called_out_sick_shifts: { label: "Called Out Sick (Explicit)", format: "number" },
  called_out_other_shifts: { label: "Called Out Other (Explicit)", format: "number" },
  no_call_no_show_shifts: { label: "No-Show (Explicit)", format: "number" },
  manager_cancelled_shifts: { label: "Manager Cancelled Shifts (Explicit)", format: "number" },
  unscheduled_shifts_worked: { label: "Unscheduled Shifts Worked", format: "number" },
  early_clockins: { label: "Early Clock-ins", format: "number" },
  late_clockins: { label: "Late Clock-ins", format: "number" },
  early_clockouts: { label: "Early Clock-outs", format: "number" },
  late_clockouts: { label: "Late Clock-outs", format: "number" },
  actual_to_scheduled_hours: { label: "Actual to Scheduled Hours", format: "percentage" },
  scheduled_to_target_hours: { label: "Scheduled to Target Hours", format: "percentage" },
  actual_to_target_hours: { label: "Actual to Target Hours", format: "percentage" },
  attendance_rate: { label: "Attendance Rate", format: "percentage" },
};

// Fallback labels for store metrics (used when availableMetrics doesn't include them)
const STORE_METRICS_LABELS = {
  forecasted_sales: { label: "Forecasted Sales", format: "dollars" },
  forecasted_adjusted_sales: { label: "Forecasted Adjusted Sales", format: "dollars" },
  actual_sales: { label: "Actual Sales", format: "dollars" },
  actual_sales_previous_year: { label: "Actual Sales (Previous Year)", format: "dollars" },
  actual_labor_cost: { label: "Actual Labor Cost", format: "dollars" },
  actual_sales_minus_labor_cost: { label: "Actual Sales - Labor Cost", format: "dollars" },
  labor_percent: { label: "Labor Percent", format: "percentage" },
  splh: { label: "Actual SPLH", format: "dollars" },
  forecasted_splh: { label: "Forecasted SPLH", format: "dollars" },
  scheduled_splh: { label: "Scheduled SPLH", format: "dollars" },
  actual_sales_vs_actual_sellable_hours: { label: "Actual Sales vs Actual Sellable Hours", format: "dollars" },
  earned_splh: { label: "Earned SPLH", format: "dollars" },
  scheduled_labor_hours: { label: "Scheduled Hours", format: "hours" },
  forecasted_labor_hours: { label: "Forecasted Hours", format: "hours" },
  actual_labor_hours: { label: "Actual Hours", format: "hours" },
  earned_labor_hours: { label: "Earned Hours", format: "hours" },
  logbook_entries: { label: "Logbook Entries", format: "number" },
  actual_to_forecasted_sales: { label: "Actual to Forecasted Sales", format: "percentage" },
  actual_to_forecasted_adjusted_sales: { label: "Actual to Forecasted Adjusted Sales", format: "percentage" },
  scheduled_to_forecasted_hours: { label: "Scheduled to Forecasted Hours", format: "percentage" },
  actual_to_earned_hours: { label: "Actual to Earned Hours", format: "percentage" },
  actual_to_scheduled_hours: { label: "Actual to Scheduled Hours", format: "percentage" },
  earned_to_forecasted_hours: { label: "Earned to Forecasted Hours", format: "percentage" },
};

const savedReportsModalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  maxWidth: "90vw",
  maxHeight: "96vh",
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

function SavedReportsModal({
  open,
  onClose,
  savedReports,
  savedReportsLoading,
  storeNames,
  availableMetrics,
  onDeleteReport,
  onReorderReports,
  onLoadReport = null, // Optional - only provided in ReportingDisplay
  showLoadButton = true, // Controls whether to show load button
  title = "Saved Reports To Dashboard",
  description = "You can go to the Dashboard tab in the sidebar to view all of these reports in one display."
}) {

  const handleOnDragEnd = (result) => {
    if (!result.destination) return;
    onReorderReports(result);
  };

  // Helper functions for report data
  const getMetricInfoForReport = (metricValue) => {
    if (!metricValue) {
      return { label: "Unknown Metric", format: "number" };
    }

    let baseValue = metricValue;
    let suffix = "";

    if (metricValue.endsWith("_previous_year")) {
      baseValue = metricValue.replace("_previous_year", "");
      suffix = " (Prev Year)";
    }

    const metric = availableMetrics.find((m) => m.value === baseValue);

    if (metric) {
      return {
        label: `${metric.label}${suffix}`,
        format: metric.format,
      };
    }

    // Fallback to employee metrics labels
    const employeeMetric = EMPLOYEE_METRICS_LABELS[baseValue];
    if (employeeMetric) {
      return {
        label: `${employeeMetric.label}${suffix}`,
        format: employeeMetric.format,
      };
    }

    // Fallback to store metrics labels
    const storeMetric = STORE_METRICS_LABELS[baseValue];
    if (storeMetric) {
      return {
        label: `${storeMetric.label}${suffix}`,
        format: storeMetric.format,
      };
    }

    return { label: metricValue, format: "number" };
  };

  const formatDateRangeForDisplay = (dateRangeString) =>
    getDateRangeDisplayLabel(dateRangeString);

  const getStoreNamesForReport = (report) => {
    if (!storeNames || !report.selected_stores) {
      return "No stores selected";
    }
    return storeNames
      .filter(store => report.selected_stores.includes(store.id))
      .map(store => store.store_name)
      .join(", ");
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="saved-reports-modal"
      aria-describedby="saved-reports-description"
    >
      <Box sx={savedReportsModalStyle}>
        <div className="absolute right-2 top-2">
          <IconButton
            className="text-gray-400 hover:text-gray-600"
            onClick={onClose}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>

        <div>
          <div className="mb-6">
            <div className="flex items-center space-x-3 mb-2">
              <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
            </div>
            <p className="text-sm text-gray-600">
              {description}
            </p>
          </div>

          <div className="max-h-[620px] overflow-y-auto">
            {savedReportsLoading ? (
              <div className="text-center py-8">
                <div className="flex items-center justify-center">
                  <LoadSpinner />
                </div>
                <p className="text-gray-500 mt-2">Loading saved reports...</p>
              </div>
            ) : savedReports.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No saved reports found. Create and save a report to see it here.
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <DragDropContext onDragEnd={handleOnDragEnd}>
                  <Droppable droppableId="saved-reports">
                    {(provided) => (
                      <div
                        {...provided.droppableProps}
                        ref={provided.innerRef}
                        className="divide-y divide-gray-200"
                      >
                        {savedReports.map((report, index) => {
                          const compareLocationsChecked = report.selected_radio_option === "Compare by Store" ||
                            report.selected_radio_option === "Compare by Employee" ||
                            report.selected_radio_option === "Compare by Role";
                          const compareDow = report.selected_radio_option === "Compare by Day Of Week";
                          const compareMetricsChecked = report.selected_radio_option === "Compare by Metric";

                          const getComparisonLabel = () => {
                            switch (report.selected_radio_option) {
                              case "Compare by Store": return "Comparing Stores";
                              case "Compare by Employee": return "Comparing Employees";
                              case "Compare by Role": return "Comparing Roles";
                              case "Compare by Metric": return "Comparing Metrics";
                              default: return "Comparing";
                            }
                          };

                          return (
                            <Draggable
                              key={report.id}
                              draggableId={`report-${report.id}`}
                              index={index}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={{
                                    ...provided.draggableProps.style,
                                    left: "auto !important",
                                    top: "auto !important",
                                  }}
                                  className={`flex items-center gap-4 p-4 transition-all ${
                                    snapshot.isDragging
                                      ? "bg-violet-50 border-violet-200 shadow-lg z-50"
                                      : "bg-white hover:bg-gray-50"
                                  }`}
                                >
                                  <div
                                    className="text-gray-400 cursor-grab hover:text-gray-600 transition-colors"
                                    {...provided.dragHandleProps}
                                  >
                                    <DragIndicatorIcon />
                                  </div>

                                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                    report.report_type === "logbook"
                                      ? "bg-orange-100"
                                      : report.report_type === "employee"
                                      ? "bg-teal-100"
                                      : "bg-violet-100"
                                  }`}>
                                    <span className={`text-sm font-semibold ${
                                      report.report_type === "logbook"
                                        ? "text-orange-700"
                                        : report.report_type === "employee"
                                        ? "text-teal-700"
                                        : "text-violet-700"
                                    }`}>
                                      {index + 1}
                                    </span>
                                  </div>

                                  <div className="flex-1">
                                    <div className="min-w-0">
                                      <h4 className="font-medium text-gray-900 truncate">{report.name}</h4>
                                      <p className="text-sm text-gray-600 break-words">
                                        {getStoreNamesForReport(report)}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        Time Period: {formatDateRangeForDisplay(report.selected_date_range)}
                                      </p>
                                      {report.report_type === "logbook" ? (
                                        <p className="text-xs">
                                          <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 rounded font-medium">
                                            Logbook
                                          </span>
                                        </p>
                                      ) : report.report_type === "employee" ? (
                                        <p className="text-xs text-gray-500 break-words">
                                          {report.selected_metrics?.length === 1
                                            ? `Metric: ${getMetricInfoForReport(report.selected_metrics[0])?.label || "Unknown"}`
                                            : `Metrics: ${report.selected_metrics?.map(m => getMetricInfoForReport(m).label).join(", ")}`}
                                        </p>
                                      ) : (
                                        <p className="text-xs text-gray-500 break-words">
                                          {report.selected_metrics?.length === 1
                                            ? `Metric: ${getMetricInfoForReport(report.selected_metrics[0])?.label || "Unknown"}`
                                            : `Metrics: ${report.selected_metrics?.map(m => getMetricInfoForReport(m).label).join(", ")}`}
                                        </p>
                                      )}
                                      {report.report_type !== "logbook" && (
                                        <p className="text-xs text-blue-600 font-medium">
                                          {(compareLocationsChecked || compareDow || compareMetricsChecked)
                                            ? `${(compareLocationsChecked || compareMetricsChecked) ? getComparisonLabel() : "Comparing Days of Week"} • ${report.active_tab || "Daily"}`
                                            : report.active_tab || "Daily"}
                                        </p>
                                      )}
                                      {report.report_type === "employee" && (
                                        <p className="text-xs mt-1">
                                          <span className="inline-block px-2 py-0.5 bg-teal-100 text-teal-700 rounded font-medium">
                                            Employee
                                          </span>
                                        </p>
                                      )}
                                    </div>
                                  </div>

                                  <div className="flex gap-2 flex-shrink-0 items-start">
                                    {showLoadButton && onLoadReport && (
                                      <button
                                        className="px-3 py-1.5 text-sm rounded-md text-violet-600 hover:bg-violet-50 transition-colors whitespace-nowrap"
                                        onClick={() => onLoadReport(report)}
                                      >
                                        Load
                                      </button>
                                    )}
                                    <IconButton
                                      size="small"
                                      onClick={() => onDeleteReport(report.id)}
                                      className="text-red-500 hover:bg-red-50 flex-shrink-0"
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          );
                        })}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
}

export default SavedReportsModal;
